import {memo, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {mb, pb} from "@pg-design/helpers-css";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, QueryValues} from "@pg-mono/form";
import {isEmpty} from "@pg-mono/nodash";
import {numberFormat} from "@pg-mono/string-utils";

import {useShouldShowPriceByOfferConfigAndUser} from "../../../../hooks/use_should_show_price_by_offer_config_and_user";
import {propertyListSortOptions} from "../../../../property/constants/property_list_sort_options";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../../property_list/PropertyListTabSwitcher";
import {Country} from "../../../../region/types/Country";
import {getLocalStoragePropertyFilters, updateLocalStoragePropertyFilters} from "../../../../search/utils/use_localstorage_property_filters";
import {useAppSelector} from "../../../../store/hooks";
import {FilterPresets, useUserPreferencesFilters} from "../../../../user/hooks/use_user_preferences_filters";
import {getCurrency} from "../../../../utils/get_currency";
import {getPlanner3dData} from "../../../constants/get_planner_3d_data";
import {getOfferTypeNamePlural} from "../../../helpers/OfferType";
import {hitPropertyListViewChange} from "../../../tracking/hit_property_list_view_change";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {OfferPriceType} from "../../../types/OfferPriceType";
import {fetchOfferDetailLowestPriceProperty} from "../../actions/fetch_offer_detail_lowest_price_property";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {Planner3dModal} from "../../planner3D/components/Planner3D";
import {getOfferDetailPropertyListQueryFilters} from "../../utils/getOfferDetailPropertyListQueryFilters";
import {OfferDetailPropertyListFilters} from "./OfferDetailPropertyListFilters";
import {OfferDetailPropertyListTable, propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";
import {OfferDetailPropertyListTiles} from "./OfferDetailPropertyListTiles";

interface IProps {
    offerId: number;
    offer: IOfferDetail;
    propertyListObj: IPropertyListObj;
    onFilterValuesChange: (values: ISearchFiltersFormValues) => void;
    propertyListType?: PropertyListType;
    gtmMeta?: {
        listId: string;
        listName: string;
    };
}

export interface IMainImage {
    m_img_224x147?: string;
    m_img_375x211?: string;
    m_img_760x428?: string;
    m_img_1500?: string;
    m_img_155x87?: string;
}

export const initialPropertiesFilterValues: ISearchFiltersFormValues = {
    area: {lower: "", upper: ""},
    price: {lower: "", upper: ""},
    rooms: {lower: "", upper: ""},
    floor_choices: [],
    house_storeys: []
};

export const OfferDetailPropertyList = memo((props: IProps) => {
    const {offerId, offer, propertyListObj, onFilterValuesChange, propertyListType, gtmMeta} = props;

    const propertiesData = useAppSelector((store) => store.offerDetail.properties);
    const activeTab = useAppSelector((store) => store.offerDetail.properties.activeTab);
    const currentProperties = propertiesData[activeTab];
    const count = currentProperties.count;

    const [currentPropertyListType, setCurrentPropertyListType] = useState(propertyListType ?? PropertyListType.TABLE);
    const isApartment = offer?.is_holiday_location || offer?.is_condohotel;
    const [filterValues, setFilterValues] = useState(initialPropertiesFilterValues);

    const hasPlanner3D = Boolean(getPlanner3dData(offer.id));
    const [showPropertyListBlend, setShowPropertyListBlend] = useState(hasPlanner3D);

    const dispatch = useDispatch();

    const userPreferencesFilters = useUserPreferencesFilters(FilterPresets.WithoutRegion);
    /*
     * filter values for property list - either based on user preferences or temporary filters from local storage
     */
    const localStoragePropertyFilters = getLocalStoragePropertyFilters();
    const initialQueryValues: QueryValues = isEmpty(localStoragePropertyFilters) ? userPreferencesFilters : localStoragePropertyFilters;
    const initialPropertyListQueryFilters = getOfferDetailPropertyListQueryFilters(initialQueryValues, offer.type);

    /*
        Initial clientside data fetch
    */
    useEffect(() => {
        const searchFilters = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, initialPropertyListQueryFilters);

        setFilterValues(searchFilters);

        if (offerId) {
            // fetch filtered properties only when initial filters has been set
            if (!isEmpty(initialQueryValues)) {
                const defaultQueryValues = {page: 1};
                const propertyListQuery = {...initialPropertyListQueryFilters, type: offer.type};

                fetchOfferDetailProperties(
                    offerId,
                    PropertyListTabTypes.FILTER,
                    OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                    defaultQueryValues,
                    propertyListQuery
                )(dispatch);

                dispatch(fetchOfferDetailLowestPriceProperty(offerId, propertyListQuery));
            }

            // clear ls stored filter after fetching, we don't want to reuse it
            updateLocalStoragePropertyFilters({});
        }
    }, []);

    useEffect(() => {
        onFilterValuesChange(filterValues);
    }, [filterValues]);

    // Reset filters when switching to INVESTMENT tab (investment properties with ROI don't support filtering)
    useEffect(() => {
        if (activeTab === PropertyListTabTypes.INVESTMENT) {
            setFilterValues(initialPropertiesFilterValues);
        }
    }, [activeTab]);

    const getOfferTypeName = () => {
        const offerTypeName = getOfferTypeNamePlural(offer.type);
        return offerTypeName.charAt(0).toUpperCase() + offerTypeName.slice(1);
    };

    const changeListType = (type: PropertyListType) => {
        hitPropertyListViewChange(type === PropertyListType.TABLE ? "lista" : "rzuty");
        setCurrentPropertyListType(type);
    };

    const offerRangePrice = (offer.region.country === Country.SPAIN && offer?.stats?.ranges_price_min) || offer.stats?.ranges_price_m2_min || null;

    const priceType = offer.region.country === Country.SPAIN && offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const propertyListHeadline = `${isApartment ? "Apartamenty" : getOfferTypeName()} w inwestycji`;
    const propertyListName = gtmMeta?.listName ?? propertyListHeadline;
    const propertyListId = gtmMeta?.listId ?? `${isApartment ? "apartamenty" : getOfferTypeName().toLowerCase()}_w_inwestycji`;

    return (
        <>
            <PropertyListWrapper showBlend={showPropertyListBlend && propertyListObj.properties?.length > 0}>
                {offer && (
                    <>
                        <Text variant="headline_4" as="h2" css={[pb(1)]}>
                            {propertyListHeadline}
                            {` (${count})`}
                        </Text>

                        {shouldShowPrice && (offer.region.country === Country.SPAIN || offer.region.country === Country.POLAND) && offerRangePrice && (
                            <Text variant="headline_5" as="h3" css={[pb(2)]}>
                                od{" "}
                                <span className="bt">
                                    <Highlight>
                                        <Text variant="headline_2" as="span">
                                            {`${numberFormat(offerRangePrice)}${priceType} ${getCurrency({
                                                currency: offer.currency
                                            })}`}
                                        </Text>

                                        {offer.region.country === Country.SPAIN && offer.stats?.ranges_price_min ? null : (
                                            <>
                                                /m<sup>2</sup>
                                            </>
                                        )}
                                    </Highlight>
                                </span>
                            </Text>
                        )}
                    </>
                )}

                <OfferDetailPropertyListFilters
                    propertyListObj={propertyListObj}
                    filterValues={filterValues}
                    offerId={offerId}
                    offer={offer}
                    setFilterValues={setFilterValues}
                    sortOptions={propertyListSortOptions}
                    currentPropertyListType={currentPropertyListType}
                    setCurrentPropertyListType={changeListType}
                />

                <div css={currentPropertyListType === PropertyListType.TILES ? showList : hideList}>
                    <OfferDetailPropertyListTiles
                        filterValues={filterValues}
                        propertyListObj={propertyListObj}
                        offer={offer}
                        gtmMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                </div>

                <div css={currentPropertyListType === PropertyListType.TABLE ? showList : hideList}>
                    <OfferDetailPropertyListTable
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        propertyListObj={propertyListObj}
                        offerId={offerId}
                        offer={offer}
                        gtmMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                </div>
            </PropertyListWrapper>

            {hasPlanner3D && (
                <div css={buttonsWrapper}>
                    <Planner3dModal offer={offer} vendorId={offer.vendor.id} css={planner3dButtonStyle} routedModalId="detail">
                        <Button variant="filled_secondary">Plan pięter</Button>
                    </Planner3dModal>

                    <ShowMoreButton variant="filled_secondary" onClick={() => setShowPropertyListBlend(false)} hide={!showPropertyListBlend}>
                        Pokaż więcej rzutów
                    </ShowMoreButton>
                </div>
            )}
        </>
    );
});

const PropertyListWrapper = styled.section<{showBlend: boolean}>`
    position: relative;
    z-index: 1;
    transition: max-height 0.4s ease-in-out;

    ${mb(3)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        ${mb(3)};
    }

    ${(props) =>
        props.showBlend
            ? css`
                  max-height: 1300px;
                  overflow-y: hidden;

                  &:after {
                      content: "";
                      position: absolute;
                      bottom: 0;
                      left: 0;
                      width: 100%;
                      height: 10rem;
                      background: linear-gradient(rgba(255, 255, 255, 0), ${props.theme.colors.gray["100"]});
                  }

                  @media (min-width: ${props.theme.breakpoints.md}) {
                      max-height: 744px;
                  }
              `
            : css`
                  max-height: 8000px;

                  @media (min-width: ${props.theme.breakpoints.md}) {
                      max-height: 3000px;
                  }
              `};
`;

const showList = css`
    display: block;
`;

const hideList = css`
    display: none;
`;

const buttonsWrapper = css`
    display: flex;
    gap: 2rem;
    justify-content: center;
`;

const planner3dButtonStyle = (theme: Theme) => css`
    display: flex;
    justify-content: center;
    ${mb(3)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;

const ShowMoreButton = styled(Button)<{hide: boolean}>`
    display: ${(props) => (props.hide ? "none" : "block")};
`;
import {memo, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {mb, pb} from "@pg-design/helpers-css";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, QueryValues} from "@pg-mono/form";
import {isEmpty} from "@pg-mono/nodash";
import {numberFormat} from "@pg-mono/string-utils";

import {useShouldShowPriceByOfferConfigAndUser} from "../../../../hooks/use_should_show_price_by_offer_config_and_user";
import {propertyListSortOptions} from "../../../../property/constants/property_list_sort_options";
import {IPropertyListObj} from "../../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../../property_list/PropertyListTabSwitcher";
import {Country} from "../../../../region/types/Country";
import {getLocalStoragePropertyFilters, updateLocalStoragePropertyFilters} from "../../../../search/utils/use_localstorage_property_filters";
import {useAppSelector} from "../../../../store/hooks";
import {FilterPresets, useUserPreferencesFilters} from "../../../../user/hooks/use_user_preferences_filters";
import {getCurrency} from "../../../../utils/get_currency";
import {getPlanner3dData} from "../../../constants/get_planner_3d_data";
import {getOfferTypeNamePlural} from "../../../helpers/OfferType";
import {hitPropertyListViewChange} from "../../../tracking/hit_property_list_view_change";
import {IOfferDetail} from "../../../types/IOfferDetail";
import {OfferPriceType} from "../../../types/OfferPriceType";
import {fetchOfferDetailLowestPriceProperty} from "../../actions/fetch_offer_detail_lowest_price_property";
import {fetchOfferDetailProperties, PropertyListTabTypes} from "../../actions/fetch_offer_detail_properties";
import {OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE} from "../../constants/offer_detail_properties";
import {Planner3dModal} from "../../planner3D/components/Planner3D";
import {getOfferDetailPropertyListQueryFilters} from "../../utils/getOfferDetailPropertyListQueryFilters";
import {OfferDetailPropertyListFilters} from "./OfferDetailPropertyListFilters";
import {OfferDetailPropertyListTable, propertyListTableFiltersFields} from "./OfferDetailPropertyListTable";
import {OfferDetailPropertyListTiles} from "./OfferDetailPropertyListTiles";

interface IProps {
    offerId: number;
    offer: IOfferDetail;
    propertyListObj: IPropertyListObj;
    onFilterValuesChange: (values: ISearchFiltersFormValues) => void;
    propertyListType?: PropertyListType;
    gtmMeta?: {
        listId: string;
        listName: string;
    };
}

export interface IMainImage {
    m_img_224x147?: string;
    m_img_375x211?: string;
    m_img_760x428?: string;
    m_img_1500?: string;
    m_img_155x87?: string;
}

export const initialPropertiesFilterValues: ISearchFiltersFormValues = {
    area: {lower: "", upper: ""},
    price: {lower: "", upper: ""},
    rooms: {lower: "", upper: ""},
    floor_choices: [],
    house_storeys: []
};

export const OfferDetailPropertyList = memo((props: IProps) => {
    const {offerId, offer, propertyListObj, onFilterValuesChange, propertyListType, gtmMeta} = props;

    const propertiesData = useAppSelector((store) => store.offerDetail.properties);
    const activeTab = useAppSelector((store) => store.offerDetail.properties.activeTab);
    const currentProperties = propertiesData[activeTab];
    const count = currentProperties.count;

    const [currentPropertyListType, setCurrentPropertyListType] = useState(propertyListType ?? PropertyListType.TABLE);
    const isApartment = offer?.is_holiday_location || offer?.is_condohotel;
    const [filterValues, setFilterValues] = useState(initialPropertiesFilterValues);

    const hasPlanner3D = Boolean(getPlanner3dData(offer.id));
    const [showPropertyListBlend, setShowPropertyListBlend] = useState(hasPlanner3D);

    const dispatch = useDispatch();

    const userPreferencesFilters = useUserPreferencesFilters(FilterPresets.WithoutRegion);
    /*
     * filter values for property list - either based on user preferences or temporary filters from local storage
     */
    const localStoragePropertyFilters = getLocalStoragePropertyFilters();
    const initialQueryValues: QueryValues = isEmpty(localStoragePropertyFilters) ? userPreferencesFilters : localStoragePropertyFilters;
    const initialPropertyListQueryFilters = getOfferDetailPropertyListQueryFilters(initialQueryValues, offer.type);

    /*
        Initial clientside data fetch
    */
    useEffect(() => {
        const searchFilters = fromQueryValues<ISearchFiltersFormValues>(propertyListTableFiltersFields, initialPropertyListQueryFilters);

        setFilterValues(searchFilters);

        if (offerId) {
            // fetch filtered properties only when initial filters has been set
            if (!isEmpty(initialQueryValues)) {
                const defaultQueryValues = {page: 1};
                const propertyListQuery = {...initialPropertyListQueryFilters, type: offer.type};

                fetchOfferDetailProperties(
                    offerId,
                    PropertyListTabTypes.FILTER,
                    OFFER_DETAIL_PROPERTY_LIST_DEFAULT_PAGE_SIZE,
                    defaultQueryValues,
                    propertyListQuery
                )(dispatch);

                dispatch(fetchOfferDetailLowestPriceProperty(offerId, propertyListQuery));
            }

            // clear ls stored filter after fetching, we don't want to reuse it
            updateLocalStoragePropertyFilters({});
        }
    }, []);

    useEffect(() => {
        onFilterValuesChange(filterValues);
    }, [filterValues]);

    // Reset filters when switching to INVESTMENT tab (investment properties with ROI don't support filtering)
    useEffect(() => {
        if (activeTab === PropertyListTabTypes.INVESTMENT) {
            setFilterValues(initialPropertiesFilterValues);
        }
    }, [activeTab]);

    const getOfferTypeName = () => {
        const offerTypeName = getOfferTypeNamePlural(offer.type);
        return offerTypeName.charAt(0).toUpperCase() + offerTypeName.slice(1);
    };

    const changeListType = (type: PropertyListType) => {
        hitPropertyListViewChange(type === PropertyListType.TABLE ? "lista" : "rzuty");
        setCurrentPropertyListType(type);
    };

    const offerRangePrice = (offer.region.country === Country.SPAIN && offer?.stats?.ranges_price_min) || offer.stats?.ranges_price_m2_min || null;

    const priceType = offer.region.country === Country.SPAIN && offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const propertyListHeadline = `${isApartment ? "Apartamenty" : getOfferTypeName()} w inwestycji`;
    const propertyListName = gtmMeta?.listName ?? propertyListHeadline;
    const propertyListId = gtmMeta?.listId ?? `${isApartment ? "apartamenty" : getOfferTypeName().toLowerCase()}_w_inwestycji`;

    return (
        <>
            <PropertyListWrapper showBlend={showPropertyListBlend && propertyListObj.properties?.length > 0}>
                {offer && (
                    <>
                        <Text variant="headline_4" as="h2" css={[pb(1)]}>
                            {propertyListHeadline}
                            {` (${count})`}
                        </Text>

                        {shouldShowPrice && (offer.region.country === Country.SPAIN || offer.region.country === Country.POLAND) && offerRangePrice && (
                            <Text variant="headline_5" as="h3" css={[pb(2)]}>
                                od{" "}
                                <span className="bt">
                                    <Highlight>
                                        <Text variant="headline_2" as="span">
                                            {`${numberFormat(offerRangePrice)}${priceType} ${getCurrency({
                                                currency: offer.currency
                                            })}`}
                                        </Text>

                                        {offer.region.country === Country.SPAIN && offer.stats?.ranges_price_min ? null : (
                                            <>
                                                /m<sup>2</sup>
                                            </>
                                        )}
                                    </Highlight>
                                </span>
                            </Text>
                        )}
                    </>
                )}

                <OfferDetailPropertyListFilters
                    propertyListObj={propertyListObj}
                    filterValues={filterValues}
                    offerId={offerId}
                    offer={offer}
                    setFilterValues={setFilterValues}
                    sortOptions={propertyListSortOptions}
                    currentPropertyListType={currentPropertyListType}
                    setCurrentPropertyListType={changeListType}
                />

                <div css={currentPropertyListType === PropertyListType.TILES ? showList : hideList}>
                    <OfferDetailPropertyListTiles
                        filterValues={filterValues}
                        propertyListObj={propertyListObj}
                        offer={offer}
                        gtmMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                </div>

                <div css={currentPropertyListType === PropertyListType.TABLE ? showList : hideList}>
                    <OfferDetailPropertyListTable
                        filterValues={filterValues}
                        setFilterValues={setFilterValues}
                        propertyListObj={propertyListObj}
                        offerId={offerId}
                        offer={offer}
                        gtmMeta={{
                            listId: propertyListId,
                            listName: propertyListName
                        }}
                    />
                </div>
            </PropertyListWrapper>

            {hasPlanner3D && (
                <div css={buttonsWrapper}>
                    <Planner3dModal offer={offer} vendorId={offer.vendor.id} css={planner3dButtonStyle} routedModalId="detail">
                        <Button variant="filled_secondary">Plan pięter</Button>
                    </Planner3dModal>

                    <ShowMoreButton variant="filled_secondary" onClick={() => setShowPropertyListBlend(false)} hide={!showPropertyListBlend}>
                        Pokaż więcej rzutów
                    </ShowMoreButton>
                </div>
            )}
        </>
    );
});

const PropertyListWrapper = styled.section<{showBlend: boolean}>`
    position: relative;
    z-index: 1;
    transition: max-height 0.4s ease-in-out;

    ${mb(3)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        ${mb(3)};
    }

    ${(props) =>
        props.showBlend
            ? css`
                  max-height: 1300px;
                  overflow-y: hidden;

                  &:after {
                      content: "";
                      position: absolute;
                      bottom: 0;
                      left: 0;
                      width: 100%;
                      height: 10rem;
                      background: linear-gradient(rgba(255, 255, 255, 0), ${props.theme.colors.gray["100"]});
                  }

                  @media (min-width: ${props.theme.breakpoints.md}) {
                      max-height: 744px;
                  }
              `
            : css`
                  max-height: 8000px;

                  @media (min-width: ${props.theme.breakpoints.md}) {
                      max-height: 3000px;
                  }
              `};
`;

const showList = css`
    display: block;
`;

const hideList = css`
    display: none;
`;

const buttonsWrapper = css`
    display: flex;
    gap: 2rem;
    justify-content: center;
`;

const planner3dButtonStyle = (theme: Theme) => css`
    display: flex;
    justify-content: center;
    ${mb(3)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;

const ShowMoreButton = styled(Button)<{hide: boolean}>`
    display: ${(props) => (props.hide ? "none" : "block")};
`;
